import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Category as CategoryIcon,
  Image as ImageIcon,
  FolderOpen as EmptyIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Category, CategoryFormData } from '../../types/models';
import { categoryService, imageService } from '../../services/firebase';
import { logger } from '../../services/loggerService';
import { useAuditLog } from '../../hooks/useAuditLog';
import { AuditAction } from '../../types/audit';

// Validation schema
const categorySchema: yup.ObjectSchema<CategoryFormData> = yup.object({
  name: yup.string().required('Category name is required').min(2, 'Name must be at least 2 characters'),
  description: yup.string().optional(),
  image: yup.string().optional(),
  isActive: yup.boolean().required(),
  sortOrder: yup.number().required('Sort order is required').min(0, 'Sort order must be positive'),
});

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  
  const { logCategoryAction } = useAuditLog();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<CategoryFormData>({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      sortOrder: categories.length + 1
    }
  });

  const loadCategories = async () => {
    setLoading(true);
    try {
      const categoriesData = await categoryService.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      logger.error('Error loading categories', error, { component: 'Categories', action: 'loadCategories' });
      setSnackbar({ open: true, message: 'Failed to load categories', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setImagePreview(category.image || null);
      reset({
        name: category.name,
        description: category.description || '',
        isActive: category.isActive,
        sortOrder: category.sortOrder
      });
    } else {
      setEditingCategory(null);
      setImagePreview(null);
      reset({
        name: '',
        description: '',
        isActive: true,
        sortOrder: categories.length + 1
      });
    }
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setImageFile(null);
    setImagePreview(null);
    reset();
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, category: Category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCategory(null);
  };

  const onSubmit = async (data: CategoryFormData) => {
    try {
      setUploadingImage(true);
      let imageUrl = editingCategory?.image || '';

      // Upload new image if selected
      if (imageFile) {
        imageUrl = await imageService.uploadImage(imageFile, 'categories');
      }

      const categoryData = {
        ...data,
        image: imageUrl || undefined
      };

      if (editingCategory) {
        // Update existing category
        await categoryService.updateCategory(editingCategory.id, categoryData);
        
        const updatedCategory: Category = {
          ...editingCategory,
          ...categoryData,
          updatedAt: new Date()
        };
        
        setCategories(prev => prev.map(cat => cat.id === editingCategory.id ? updatedCategory : cat));
        
        // Audit log
        await logCategoryAction(
          AuditAction.CATEGORY_UPDATE,
          editingCategory.id,
          data.name,
          { before: editingCategory, after: categoryData },
          true
        );
        
        setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
        logger.info('Category updated successfully', { categoryId: editingCategory.id, categoryName: data.name });
      } else {
        // Create new category
        const newCategoryData = {
          ...categoryData,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        const id = await categoryService.addCategory(newCategoryData);
        
        const newCategory: Category = {
          id,
          ...newCategoryData
        };
        
        setCategories(prev => [...prev, newCategory]);
        
        // Audit log
        await logCategoryAction(
          AuditAction.CATEGORY_CREATE,
          id,
          data.name,
          undefined,
          true
        );
        
        setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
        logger.info('Category created successfully', { categoryId: id, categoryName: data.name });
      }
      
      handleCloseDialog();
    } catch (error) {
      logger.error('Error saving category', error, { component: 'Categories', action: 'onSubmit' });
      setSnackbar({ open: true, message: 'Error saving category', severity: 'error' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      // Delete image from storage if exists (don't let this block category deletion)
      if (categoryToDelete.image) {
        logger.debug('Deleting category image', { imageUrl: categoryToDelete.image });
        try {
          await imageService.deleteImage(categoryToDelete.image);
          logger.info('Category image deleted successfully', { imageUrl: categoryToDelete.image });
        } catch (imageError) {
          logger.warn('Failed to delete category image, but continuing with category deletion', imageError);
        }
      }
      
      // Delete the category from Firestore
      await categoryService.deleteCategory(categoryToDelete.id);
      
      setCategories(prev => prev.filter(cat => cat.id !== categoryToDelete.id));
      
      // Audit log
      await logCategoryAction(
        AuditAction.CATEGORY_DELETE,
        categoryToDelete.id,
        categoryToDelete.name,
        { before: categoryToDelete },
        true
      );
      
      setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
      logger.info('Category deleted successfully', { categoryId: categoryToDelete.id, categoryName: categoryToDelete.name });
    } catch (error) {
      logger.error('Error deleting category', error, { component: 'Categories', action: 'handleDeleteConfirm' });
      setSnackbar({ open: true, message: 'Error deleting category', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      await categoryService.updateCategory(category.id, { isActive: !category.isActive });
      
      const updatedCategory = { ...category, isActive: !category.isActive, updatedAt: new Date() };
      
      setCategories(prev => prev.map(cat => cat.id === category.id ? updatedCategory : cat));
      
      // Audit log
      await logCategoryAction(
        AuditAction.CATEGORY_UPDATE,
        category.id,
        category.name,
        { 
          before: { isActive: category.isActive },
          after: { isActive: !category.isActive }
        },
        true
      );
      
      setSnackbar({ 
        open: true, 
        message: `Category ${updatedCategory.isActive ? 'activated' : 'deactivated'} successfully`, 
        severity: 'success' 
      });
      logger.info('Category status toggled', { categoryId: category.id, newStatus: updatedCategory.isActive });
    } catch (error) {
      logger.error('Error updating category status', error, { component: 'Categories', action: 'handleToggleStatus' });
      setSnackbar({ open: true, message: 'Error updating category status', severity: 'error' });
    }
    handleMenuClose();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body1" color="text.secondary">
            Manage product categories and organize your inventory
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Add Category
        </Button>
      </Box>

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* Empty State */}
      {!loading && categories.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            textAlign: 'center'
          }}
        >
          <EmptyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            No categories yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating your first category
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Add Category
          </Button>
        </Box>
      )}

      {/* Categories Grid */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {categories.map((category) => (
          <Box key={category.id} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '400px' }}>
            <Card 
              sx={{ 
                height: '100%',
                position: 'relative',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {/* Category Image */}
                <Box
                  sx={{
                    height: 160,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    backgroundImage: category.image ? `url(${category.image})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  {!category.image && (
                    <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                  )}
                  
                  {/* Status Chip */}
                  <Chip
                    label={category.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={category.isActive ? 'success' : 'default'}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      fontWeight: 600
                    }}
                  />

                  {/* Menu Button */}
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, category)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,1)',
                      }
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                {/* Category Info */}
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {category.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: 40
                    }}
                  >
                    {category.description || 'No description available'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Sort Order: {category.sortOrder}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.createdAt instanceof Date 
                        ? category.createdAt.toLocaleDateString() 
                        : new Date(category.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 160 }
        }}
      >
        <MenuItem onClick={() => {
          handleOpenDialog(selectedCategory!);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleToggleStatus(selectedCategory!)}>
          <ListItemIcon>
            <CategoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {selectedCategory?.isActive ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem 
          onClick={() => handleDeleteClick(selectedCategory!)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
            {editingCategory ? 'Edit Category' : 'Add New Category'}
          </DialogTitle>
          
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Category Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Box>

              <Box>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              </Box>

              {/* Image Upload Section */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Category Image
                </Typography>
                
                {(imagePreview || editingCategory?.image) && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 200,
                        height: 120,
                        border: '2px dashed #ddd',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundImage: `url(${imagePreview || editingCategory?.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255,255,255,0.8)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
                
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="image-upload"
                  type="file"
                  onChange={handleImageSelect}
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<ImageIcon />}
                    sx={{ textTransform: 'none' }}
                  >
                    {imagePreview || editingCategory?.image ? 'Change Image' : 'Upload Image'}
                  </Button>
                </label>
              </Box>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <Controller
                    name="sortOrder"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Sort Order"
                        type="number"
                        fullWidth
                        error={!!errors.sortOrder}
                        helperText={errors.sortOrder?.message}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                          color="primary"
                        />
                      }
                      label="Active"
                      sx={{ mt: 2 }}
                    />
                  )}
                />
                </Box>
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || uploadingImage}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {uploadingImage ? 'Uploading...' : (editingCategory ? 'Update' : 'Create')} Category
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Category?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete "{categoryToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;
